/**
 * Portfolio context contract: `contextData.tables` holds optional per-table overlays
 * (see portfolioApi mock + loadPortfolioContext merge). IaDashboard may clone API-shaped
 * tables through cloneTableWithPortfolioContext before setOutputText.
 */

/** Keys inside contextData.tables — stable for future AWS payloads. */
export const PORTFOLIO_TABLE_CONTEXT_KEYS = {
  "YTD Performance by index": "ytdPerformanceByIndex",
  "YTD Performance by Strategy by Index": "ytdPerformanceByIndex",
  "YTD Performance portfolio": "ytdPerformancePortfolio",
  "YTD Performance by Strategy": "ytdPerformancePortfolio",
};

/**
 * @param {string} catalogKey - Sharma table catalog key (see sharmaReportsConfig / enums TABLE_NAMES)
 * @returns {string|null}
 */
export function mapTableCatalogKeyToContextKey(catalogKey) {
  return PORTFOLIO_TABLE_CONTEXT_KEYS[catalogKey] ?? null;
}

function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Deep-clones a table definition and applies optional title / row tweaks from portfolio context.
 * @param {object} baseTable - { title, columns, rows }
 * @param {object|null} contextData
 * @param {string|null} tablesKey - e.g. ytdPerformanceByIndex
 */
export function cloneTableWithPortfolioContext(baseTable, contextData, tablesKey) {
  if (!baseTable) {
    return baseTable;
  }
  if (!tablesKey) {
    return JSON.parse(JSON.stringify(baseTable));
  }
  const slice = contextData?.tables?.[tablesKey];
  const out = JSON.parse(JSON.stringify(baseTable));
  if (!slice || typeof slice !== "object") {
    return out;
  }
  if (slice.titleSuffix && typeof slice.titleSuffix === "string") {
    out.title = `${out.title || ""}${slice.titleSuffix}`;
  }
  if (
    slice.firstFundSuffix &&
    typeof slice.firstFundSuffix === "string" &&
    Array.isArray(out.rows) &&
    out.rows[0] &&
    typeof out.rows[0].fund === "string"
  ) {
    out.rows[0] = {
      ...out.rows[0],
      fund: `${out.rows[0].fund} ${slice.firstFundSuffix}`.trim(),
    };
  }
  if (typeof slice.performanceDelta === "number" && Array.isArray(out.rows)) {
    out.rows = out.rows.map((row) => {
      if (!row || typeof row.performance !== "number") return row;
      return {
        ...row,
        performance: round2(row.performance + slice.performanceDelta),
      };
    });
  }
  return out;
}
